import { Injectable } from '@angular/core';

import { FingerprintAIO } from '@awesome-cordova-plugins/fingerprint-aio/ngx';
import { Preferences } from '@capacitor/preferences';
import { App } from '@capacitor/app';
@Injectable({
   providedIn: 'root'
})
export class BiometricService {

  private platformType: 'ios' | 'android' | 'web' = 'web';
  private readonly SKIP_BIOMETRIC_KEY = 'shouldSkipBiometric';

  constructor(
     private faio: FingerprintAIO
  ) {
    if (typeof cordova !== 'undefined') {
      this.platformType = /Android/i.test(navigator.userAgent) ? 'android' : 'ios';
    } else {
      this.platformType = 'web';
    }

    App.addListener('appStateChange', (state) => {
      if (!state.isActive) {
        this.clearShouldSkipBiometric();
      }
    });
  }

  get isNativePlatform(): boolean {
    return this.platformType === 'ios' || this.platformType === 'android';
  }

  // Nueva función para mostrar el prompt de habilitar biometría
  async askForBiometrics(): Promise<boolean> {
     return new Promise((resolve) => {
       const enableBiometrics = window.confirm(
         '¿Te gustaría activar la autenticación biométrica para iniciar sesión?'
       );
       resolve(enableBiometrics);
     });
  }

  // Función para comprobar si hay credenciales guardadas
  async getStoredCredentials(): Promise<{ email: string; password: string; logout: string; name?: string; fatherSurname?: string; motherSurname?: string; validEmail?: string; } | false> {
    try {
      const keys = ['email', 'password', 'isLogout', 'name', 'fatherSurname', 'motherSurname', 'validEmail'];
      const entries = await Promise.all(keys.map(key => Preferences.get({ key })));

      const [email, password, logout, name, fatherSurname, motherSurname, validEmail] = entries.map(entry => entry.value);

      if (!password) {
        return false;
      }

      if (email && logout) {
        return { email, password, logout, name, fatherSurname, motherSurname, validEmail };
      }

      return false;
    } catch (error) {
      console.error('Error al obtener las credenciales almacenadas:', error);
      return false;
    }
  }

  /** Función para abrir la biometrica en el dispositivo y realizar autenticación */
  async authenticateWithBiometrics(): Promise<boolean> {
     try {
       // Comprobar si la autenticación biométrica está disponible
       await this.faio.isAvailable({requireStrongBiometrics: false});
       // Mostrar la interfaz biométrica
       await this.faio.show({
         title: 'Autenticación Biométrica',
         subtitle: 'Usa tu biometría para acceder a la aplicación',
         description: 'Por favor, autentícate para continuar',
         disableBackup: true,
       });
       return true;
     } catch (error) {
       console.error('Error en la autenticación biométrica:', error);
       return false;
     }
  }

  // Nueva función para guardar credenciales
  async saveCredentials(email: string, password: string, isLogout: string): Promise<void> {
     try {
        await Preferences.set({ key: 'email', value: email });
        await Preferences.set({ key: 'password', value: password });
        await Preferences.set({ key: 'isLogout', value: isLogout });
     } catch (error) {
        console.error('Error al guardar las credenciales:', error);
     }
  }

  async saveLogout(isLogout: string): Promise<void> {
    try {
      await Preferences.remove({ key: 'GPS_LOCATION' });
      await Preferences.set({ key: 'isLogout', value: isLogout });      
    } catch (error) {
      console.log('Error al guardar el estado de logout:', error);
    }
  }

  // Nueva función para eliminar credenciales guardadas
  async deleteCredentials(): Promise<void> {
    try {
      const keys = [
        'email', 'password', 'isLogout', 'name', 'fatherSurname', 'motherSurname', 'validEmail', 'GPS_LOCATION'
      ];
  
      await Promise.all(keys.map((key) => Preferences.remove({ key })));
    } catch (error) {
      console.error('Error al eliminar las credenciales:', error);
    }
  }
  

  // Función para cambiar el valor del booleano y guardarlo
  async setShouldSkipBiometric(skip: boolean): Promise<void> {
    await this.saveShouldSkipBiometricToStorage(skip);
  }

  // Limpiar el estado de shouldSkipBiometric
  async clearShouldSkipBiometric(): Promise<void> {
    await Preferences.remove({ key: this.SKIP_BIOMETRIC_KEY });
  }

  // Guardar el estado usando Capacitor Preferences del cierre de sesión manual
  private async saveShouldSkipBiometricToStorage(skip: boolean): Promise<void> {
    await Preferences.set({
      key: this.SKIP_BIOMETRIC_KEY,
      value: JSON.stringify(skip),
    });
  }
  
  // Obtener valor para comprobar si debe saltar la autenticación biométrica
  async getShouldSkipBiometricFromStorage(): Promise<boolean> {
    const valueSkipBiometric = await Preferences.get({ key: this.SKIP_BIOMETRIC_KEY });
    return valueSkipBiometric.value ? JSON.parse(valueSkipBiometric.value) : false;
  } 

  // Comprobar si la autenticación biométrica está disponible
  async isBiometricAvailable(): Promise<boolean> {
    try {
      const available = await this.faio.isAvailable({requireStrongBiometrics: false,});
      return !!available;
    } catch (error) {
      return false;
    }
  }  

  /** Guardar datos para iniciar sesión mas rapido en dispositivos nativos */
  async saveFullName(name: string, fatherSurname: string, motherSurname: string, valid: boolean): Promise<void> {
    try {
      const entries = {
        name, fatherSurname, motherSurname, validEmail: valid.toString(),
      };
  
      await Promise.all(
        Object.entries(entries).map(([key, value]) => Preferences.set({ key, value }))
      );
    } catch (error) {
      console.error('Error al guardar los datos del usuario.', error);
    }
  }

  /** Función principal para validar si existen credenciales e iniciar sesión */
  async validateBiometrics(): Promise<boolean> {
    try {
      const credentials = await this.getStoredCredentials();
      if (!credentials) {
        return false;
      }
      const isAuthenticated = await this.authenticateWithBiometrics();
      return isAuthenticated;
    } catch (error) {
      return false;
    }
  }  
  
  /** Función para eliminar la ubicación GPS  */
  async clearStoredLocation(): Promise<void> {
    await Preferences.remove({ key: 'GPS_LOCATION' });
  }


}
